// # Filename: src/components/poker/MobilePokerTable.jsx
// Mobile (<768px) table layout: compact circular seat markers around a narrow stadium
// track, hero cards + stack pinned near the bottom, controls docked to the viewport
// bottom edge.

import React, { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import PokerCard from "./PokerCard";
import PlayerAvatarFace from "./shared/PlayerAvatarFace";
import SeatRoleChip from "./shared/SeatRoleChip";
import ChipTransferOverlay from "./shared/ChipTransferOverlay";
import { FeltInfoPanel, FeltRoundMeta } from "./shared/PokerFeltInfo";
import PokerActionControls from "./shared/PokerActionControls";
import { stadiumPerimeter, stadiumPointAtFraction, opponentSeatFraction } from "./shared/stadiumMath";
import { usePokerTableView } from "./hooks/usePokerTableView";
import useMeasuredAvailableHeight from "../../hooks/useMeasuredAvailableHeight";

const MOBILE_TABLE_STRAIGHT_HALF_PCT = 12;
const MOBILE_TABLE_RADIUS_PCT = 30;
const MOBILE_TABLE_HORIZONTAL_RADIUS_SCALE = 1.36;
const MOBILE_TABLE_ASPECT_RATIO = 1.5; // aspect-[2/3] -> height = width * 1.5
// The hero marker's avatar/label/chip stack renders slightly below the table's own
// rounded box (see the felt-clip restructure above) so it isn't cut off. This reserve
// keeps that overflow — plus a small safety margin — clear of the fixed action bar
// below, using a REAL measured gap (useMeasuredAvailableHeight) instead of a guessed
// dvh-based reserve, which is what let the table render taller than the actual visible
// viewport on iOS Safari and force the page to scroll.
const MOBILE_TABLE_BOTTOM_RESERVE_PX = 56;
const MOBILE_TABLE_HERO_FRACTION = (2 * MOBILE_TABLE_STRAIGHT_HALF_PCT + (Math.PI * MOBILE_TABLE_RADIUS_PCT) / 2) /
  stadiumPerimeter(MOBILE_TABLE_STRAIGHT_HALF_PCT, MOBILE_TABLE_RADIUS_PCT);

function MobilePocketCards({ show, style }) {
  if (!show) return null;
  return (
    <div className="pointer-events-none absolute top-1/2 z-10 flex -translate-y-1/2 gap-0.5" style={style}>
      {[0, 1].map((idx) => (
        <div key={idx} className={["origin-center", idx === 1 ? "-ml-3 rotate-6" : "-rotate-6"].join(" ")}>
          <PokerCard card="??" micro faceDown />
        </div>
      ))}
    </div>
  );
}

function mobileStadiumPositionStyle(point) {
  return {
    left: `${50 + point.yPct * MOBILE_TABLE_HORIZONTAL_RADIUS_SCALE}%`,
    top: `${50 + point.xPct}%`,
  };
}

function mobileStadiumPositionPoint(point) {
  return {
    xPct: 50 + point.yPct * MOBILE_TABLE_HORIZONTAL_RADIUS_SCALE,
    yPct: 50 + point.xPct,
  };
}

function mobileSeatPoint(index, count) {
  const fraction = opponentSeatFraction(index, count, MOBILE_TABLE_HERO_FRACTION);
  return stadiumPointAtFraction(fraction, MOBILE_TABLE_STRAIGHT_HALF_PCT, MOBILE_TABLE_RADIUS_PCT);
}

function mobileHeroSeatPositionStyle() {
  return mobileStadiumPositionStyle(
    stadiumPointAtFraction(MOBILE_TABLE_HERO_FRACTION, MOBILE_TABLE_STRAIGHT_HALF_PCT, MOBILE_TABLE_RADIUS_PCT)
  );
}

// Seats near the stadium's flat left/right edges sit in the same vertical band as the
// community card row (the row is fixed-width and horizontally centered). Cards fan
// toward the table center from the avatar, so a seat's reach toward center has to
// shrink the closer that seat is to the pure side positions (|yPct| near its max) —
// top/bottom seats keep the full reach since they're vertically far from the row.
function mobilePocketCardsStyleForPoint(point) {
  const magnitude = Math.min(Math.abs(point?.yPct || 0) / MOBILE_TABLE_RADIUS_PCT, 1);
  const maxOffsetPx = 36;
  const minOffsetPx = 12;
  const offsetPx = maxOffsetPx - (maxOffsetPx - minOffsetPx) * magnitude;
  return point?.yPct > 12 ? { left: `-${offsetPx}px` } : { right: `-${offsetPx}px` };
}

function MobileTableSeatMarkers({
  opponents,
  currentTurn,
  dealerSeat,
  smallBlindSeat,
  bigBlindSeat,
  chipCountOverrides = {},
  payoutPulseSeats = new Set(),
  registerStackAmountRef,
}) {
  if (!opponents.length) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-20">
      {opponents.slice(0, 8).map((player, idx) => {
        const seatPoint = mobileSeatPoint(idx, opponents.length);
        const active = Number(currentTurn) === Number(player.seat);
        const stillInHand = !player.folded;
        const dealer = Number(dealerSeat) === Number(player.seat);
        const payoutPulse = payoutPulseSeats.has(Number(player.seat));
        const roleLabel = dealer
          ? "D"
          : Number(player.seat) === Number(smallBlindSeat)
            ? "SB"
            : Number(player.seat) === Number(bigBlindSeat)
              ? "BB"
              : "";
        return (
          <div
            key={player.seat}
            className={[
              "absolute flex h-[56px] w-[56px] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border px-1 py-0.5 text-center",
              active
                ? "border-emerald-200/60 bg-emerald-950/90 shadow-[0_0_20px_rgba(16,185,129,0.32),0_8px_18px_rgba(0,0,0,0.34)]"
                : payoutPulse
                  ? "border-amber-200/60 bg-amber-950/88 shadow-[0_0_22px_rgba(251,191,36,0.4),0_8px_18px_rgba(0,0,0,0.34)]"
                : stillInHand
                  ? "border-cyan-200/28 bg-slate-950/82 shadow-[0_0_14px_rgba(34,211,238,0.18),0_8px_18px_rgba(0,0,0,0.3)]"
                  : "border-slate-500/18 bg-slate-950/58 shadow-[0_8px_18px_rgba(0,0,0,0.24)]",
              player.folded ? "opacity-50" : "",
            ].join(" ")}
            style={mobileStadiumPositionStyle(seatPoint)}
          >
            {roleLabel ? (
              <span
                className={[
                  "absolute -right-1.5 -top-1.5 z-40 grid h-5 min-w-5 place-items-center rounded-full border border-slate-950/55 px-0.5 text-[9px] font-black text-slate-950 shadow-[0_0_0_1px_rgba(255,255,255,0.18),0_6px_12px_rgba(0,0,0,0.38)]",
                  roleLabel === "D" ? "bg-white" : roleLabel === "SB" ? "bg-sky-300" : "bg-amber-300",
                ].join(" ")}
              >
                {roleLabel}
              </span>
            ) : null}
            {player.bet ? (
              <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-[82%] rounded-full bg-cyan-100 px-2 py-1 text-[10px] font-black leading-none text-slate-950">
                {player.bet}
              </span>
            ) : null}
            <div
              className={[
                "relative rounded-full p-[2px]",
                active
                  ? "animate-pulse bg-emerald-200 shadow-[0_0_16px_rgba(110,231,183,0.9)]"
                  : stillInHand
                    ? "bg-cyan-300/85 shadow-[0_0_10px_rgba(34,211,238,0.48)]"
                    : "bg-slate-600/35 opacity-70 grayscale",
              ].join(" ")}
            >
              <PlayerAvatarFace seat={player.seat} size="mobile" />
              <MobilePocketCards show={stillInHand} style={mobilePocketCardsStyleForPoint(seatPoint)} />
              {active ? (
                <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border border-slate-950 bg-emerald-300 shadow-[0_0_10px_rgba(110,231,183,0.85)]" />
              ) : null}
            </div>
            <div className="mt-0.5 w-full truncate text-[7px] font-black uppercase leading-none tracking-[0.02em] text-emerald-50">
              {player.name || "Player"}
            </div>
            <div
              ref={registerStackAmountRef(player.seat)}
              className="mt-0.5 w-full truncate text-[7px] font-bold leading-none text-amber-100"
            >
              {chipCountOverrides[player.seat] ?? player.chips}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MobileHeroSeatMarker({
  player,
  active,
  handNumber,
  roleLabel,
  timerRemaining,
  timerPercent,
  timerUrgent,
  pendingLabel = "",
  displayChips = null,
  payoutPulse = false,
  stackAmountRef = null,
}) {
  if (!player) return null;
  const showTimer = active && !pendingLabel && timerRemaining != null;
  const stillInHand = !player.folded;

  return (
    <div
      className="pointer-events-none absolute z-30 flex -translate-x-1/2 -translate-y-[58%] flex-col items-center"
      style={mobileHeroSeatPositionStyle()}
    >
      <div className="relative flex justify-center gap-1">
        <SeatRoleChip label={roleLabel} className="z-40 -left-6 top-1/2 -translate-y-1/2" />
        {(player.cards || []).map((card, idx) => (
          <PokerCard
            key={`${handNumber}-${player.seat || "me"}-hero-${idx}-${card}`}
            card={card}
            mobileHero
            animate={Boolean(card)}
            dealVariant="hole"
            delay={idx * 110 + 40}
          />
        ))}
        {player.folded ? (
          <span className="absolute left-1/2 top-1/2 z-40 -translate-x-1/2 -translate-y-1/2 -rotate-12 rounded-sm border border-rose-200/35 bg-rose-950/82 px-2 py-0.5 text-[8px] font-black uppercase tracking-[0.16em] text-rose-100 shadow-[0_6px_18px_rgba(0,0,0,0.42)]">
            Folded
          </span>
        ) : null}
      </div>
      <div
        className={[
          "relative mt-2 flex h-[56px] w-[56px] flex-col items-center justify-center rounded-full border px-1 py-0.5 text-center shadow-[0_10px_22px_rgba(0,0,0,0.32)]",
          active
            ? "border-emerald-200/60 bg-emerald-950/90 shadow-[0_0_20px_rgba(16,185,129,0.32),0_8px_18px_rgba(0,0,0,0.34)]"
            : payoutPulse
              ? "border-amber-200/60 bg-amber-950/88 shadow-[0_0_22px_rgba(251,191,36,0.4),0_10px_22px_rgba(0,0,0,0.32)]"
            : stillInHand
              ? "border-cyan-200/28 bg-slate-950/82 shadow-[0_0_14px_rgba(34,211,238,0.18),0_8px_18px_rgba(0,0,0,0.3)]"
              : "border-slate-500/18 bg-slate-950/58 opacity-50 shadow-[0_8px_18px_rgba(0,0,0,0.24)]",
        ].join(" ")}
      >
        {player.bet ? (
          <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-[82%] rounded-full bg-cyan-100 px-2 py-1 text-[10px] font-black leading-none text-slate-950">
            {player.bet}
          </span>
        ) : null}
        <div
          className={[
            "relative rounded-full p-[2px]",
            active
              ? "animate-pulse bg-emerald-200 shadow-[0_0_16px_rgba(110,231,183,0.9)]"
              : stillInHand
                ? "bg-cyan-300/85 shadow-[0_0_10px_rgba(34,211,238,0.48)]"
                : "bg-slate-600/35 opacity-70 grayscale",
          ].join(" ")}
        >
          <PlayerAvatarFace seat={player.seat} isMe size="mobile" />
          <MobilePocketCards show={stillInHand} style={{ right: "-28px" }} />
          {active ? (
            <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border border-slate-950 bg-emerald-300 shadow-[0_0_10px_rgba(110,231,183,0.85)]" />
          ) : null}
        </div>
        <div className="mt-0.5 w-full truncate text-[7px] font-black uppercase leading-none tracking-[0.02em] text-emerald-50">
          You
        </div>
        <div ref={stackAmountRef} className="mt-0.5 w-full truncate text-[7px] font-bold leading-none text-amber-100">
          {displayChips ?? player.chips}
        </div>
      </div>
      {showTimer ? (
        <div className="mt-1 w-10 overflow-hidden rounded-full bg-black/45">
          <div
            className={[timerUrgent ? "bg-rose-300" : "bg-emerald-200", "h-1 rounded-full transition-[width] duration-200"].join(" ")}
            style={{ width: `${timerPercent}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}

export default function MobilePokerTable({ game, handResultAnimation = null, onAction, onNextHand, onPlayAgain }) {
  const navigate = useNavigate();

  // Zero-height sentinel marking the top of the table area, and a ref on the fixed
  // action bar below it. The real pixel gap between them (not a guessed dvh reserve)
  // is what the table's max size is derived from, so it always fits the ACTUAL visible
  // viewport — including iOS Safari's expanded-address-bar (smallest) state, since this
  // measurement runs via useLayoutEffect before first paint.
  const topAnchorRef = useRef(null);
  const actionBarRef = useRef(null);
  const availableHeightPx = useMeasuredAvailableHeight(topAnchorRef, actionBarRef);
  const tableMaxWidthPx =
    availableHeightPx != null
      ? Math.max(0, (availableHeightPx - MOBILE_TABLE_BOTTOM_RESERVE_PX) / MOBILE_TABLE_ASPECT_RATIO)
      : null;

  const getChipFallbackPoint = useCallback(({ seat, isHero, opponentIndex, opponentCount }) => {
    if (isHero) {
      return mobileStadiumPositionPoint(
        stadiumPointAtFraction(MOBILE_TABLE_HERO_FRACTION, MOBILE_TABLE_STRAIGHT_HALF_PCT, MOBILE_TABLE_RADIUS_PCT)
      );
    }
    return mobileStadiumPositionPoint(mobileSeatPoint(opponentIndex, opponentCount));
  }, []);

  const view = usePokerTableView({ game, handResultAnimation, onAction, getChipFallbackPoint });
  const {
    tableRef,
    registerStackAmountRef,
    me,
    heroEliminated,
    heroFeltPlayer,
    opponents,
    displayedCommunityCards,
    chipCountOverrides,
    potCountOverride,
    chipTransferStreams,
    payoutPulseSeats,
    isMyTurn,
    minRaiseTo,
    maxRaiseTo,
    timerRemaining,
    timerPercent,
    timerUrgent,
    smallBlindSeat,
    bigBlindSeat,
    roleForSeat,
    pendingLabelForSeat,
    can,
    raiseTo,
    setRaiseTo,
    raisePanelOpen,
    setRaisePanelOpen,
    canRaiseOrAllIn,
    raisePresets,
    handleAction,
  } = view;

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-[640px] flex-col gap-2">
      <div ref={topAnchorRef} className="h-0 w-full shrink-0" aria-hidden="true" />
      <div
        ref={tableRef}
        className="relative mx-auto aspect-[2/3] w-full flex-none [container-type:inline-size]"
        style={{
          maxWidth: tableMaxWidthPx != null ? `min(100%, 600px, ${tableMaxWidthPx.toFixed(1)}px)` : "min(100%, 600px)",
        }}
      >
        {/* Decorative felt only, clipped/rounded to the pill shape. Seat markers below
            are siblings OUTSIDE this clip so the bottom ("YOU") seat — which sits right
            at the stadium's lower edge — isn't cut off; the flex column around the table
            already reserves space below it for exactly this overflow. */}
        <div className="absolute inset-0 overflow-hidden rounded-full border border-emerald-200/15 bg-[#1b1009] shadow-[0_28px_90px_rgba(0,0,0,0.5)]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_24%,rgba(120,64,24,0.5),transparent_34%),linear-gradient(90deg,rgba(40,20,10,0.96),rgba(88,45,19,0.88),rgba(28,14,8,0.96))]" />
          <div className="absolute inset-3 rounded-full border-[7px] border-[#8a4b18] bg-[radial-gradient(ellipse_at_50%_48%,rgba(22,101,52,0.98),rgba(5,76,44,0.94)_52%,rgba(3,49,32,0.98)_100%)] shadow-[inset_0_0_0_2px_rgba(250,204,21,0.25),inset_0_0_0_8px_rgba(15,23,42,0.28),inset_0_24px_58px_rgba(0,0,0,0.4),0_18px_38px_rgba(0,0,0,0.5)]" />
        </div>

        <MobileTableSeatMarkers
          opponents={opponents}
          currentTurn={game?.current_turn}
          dealerSeat={game?.dealer}
          smallBlindSeat={smallBlindSeat}
          bigBlindSeat={bigBlindSeat}
          chipCountOverrides={chipCountOverrides}
          payoutPulseSeats={payoutPulseSeats}
          registerStackAmountRef={registerStackAmountRef}
        />
        {!heroEliminated ? (
          <MobileHeroSeatMarker
            player={heroFeltPlayer}
            active={isMyTurn}
            handNumber={game?.hand_number || 1}
            roleLabel={roleForSeat(me?.seat)}
            timerRemaining={timerRemaining}
            timerPercent={timerPercent}
            timerUrgent={timerUrgent}
            pendingLabel={pendingLabelForSeat(me?.seat)}
            displayChips={chipCountOverrides[me?.seat]}
            payoutPulse={payoutPulseSeats.has(Number(me?.seat))}
            stackAmountRef={registerStackAmountRef(me?.seat)}
          />
        ) : null}

        <div className="pointer-events-none absolute inset-0 z-10">
          <div className="absolute left-1/2 top-[calc(50%-50px)] -translate-x-1/2 sm:top-[calc(50%-56px)]">
            <FeltRoundMeta game={game} />
          </div>
          <div className="absolute left-1/2 top-1/2 flex min-h-[70px] -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-1.5 px-1 py-2 sm:gap-2 sm:min-h-[86px] sm:px-4 sm:py-3">
            {Array.from({ length: 5 }).map((_, idx) => (
              <PokerCard
                key={`${game?.hand_number || 1}-${idx}-${displayedCommunityCards?.[idx] || "empty"}`}
                card={displayedCommunityCards?.[idx]}
                compact
                animate={Boolean(displayedCommunityCards?.[idx])}
                dealVariant="community"
                delay={idx * 90}
              />
            ))}
          </div>
          <div className="absolute left-1/2 top-[calc(50%+40px)] -translate-x-1/2 sm:top-[calc(50%+48px)]">
            <FeltInfoPanel game={game} displayPot={potCountOverride} />
          </div>
        </div>
        <ChipTransferOverlay streams={chipTransferStreams} />
      </div>

      <div
        ref={actionBarRef}
        className="fixed inset-x-0 bottom-0 z-40 min-h-0 flex flex-col items-center justify-center gap-2 border-t border-stone-200/[0.08] bg-[#080706]/96 px-3 pb-[calc(env(safe-area-inset-bottom)+10px)] pt-2 shadow-[0_-18px_60px_rgba(0,0,0,0.55)] backdrop-blur-xl sm:static sm:mx-auto sm:w-full sm:max-w-[980px] sm:rounded-lg sm:border sm:bg-[linear-gradient(180deg,rgba(18,15,12,0.86),rgba(7,6,5,0.96))] sm:px-3 sm:py-4 sm:shadow-none sm:backdrop-blur-0 sm:min-h-[150px] sm:gap-3"
      >
        <PokerActionControls
          game={game}
          raiseTo={raiseTo}
          setRaiseTo={setRaiseTo}
          raisePanelOpen={raisePanelOpen}
          setRaisePanelOpen={setRaisePanelOpen}
          raisePresets={raisePresets}
          minRaiseTo={minRaiseTo}
          maxRaiseTo={maxRaiseTo}
          can={can}
          canRaiseOrAllIn={canRaiseOrAllIn}
          handleAction={handleAction}
          onNextHand={onNextHand}
          onPlayAgain={onPlayAgain}
          navigate={navigate}
        />
      </div>
    </div>
  );
}
